import {
  Button,
  Flex,
  Image,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { atom, useAtom } from "jotai";
import { useEffect, useState } from "react";

import {
  getSituationList,
  ResidenceTypeModel,
  SituationModel,
} from "../../clients/KompasApi";
import { usePagination } from "../../shared/hooks/usePagination";
import { useIsAuthenticated } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";

export const currentSituationAtom = atom<SituationModel | undefined>(undefined);

interface Props {
  residence: ResidenceTypeModel;
}

export function SituationModal({ residence }: Props) {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const navigate = useNavigate();

  const { offset, limit, loadMore } = usePagination("Situations");
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const [situations, setSituations] = useState<SituationModel[]>([]);

  const [selectedSituation, setSelectedSituation] = useState<SituationModel>();
  const [currentSituation, setCurrentSituation] = useAtom(currentSituationAtom);

  useEffect(() => {
    if (!isAuthenticated) return;

    const abortController = new AbortController();

    setLoading(true);

    getSituationList({
      path: { residenceTypeId: residence.id },
      query: { Offset: offset, Limit: limit },
      signal: abortController.signal,
    })
      .then(
        ({ data, error }) => {
          if (abortController.signal.aborted) return;

          if (error) {
            console.error("Error fetching situations:", error);
            return;
          }

          if (data) {
            setSituations((prev) => {
              prev.splice(offset, limit, ...(data.items ?? []));
              return prev.slice(0, offset + limit);
            });
            setTotalCount(data.totalCount ?? 0);
          }
          setLoading(false);
        },
        (e) => {
          if (abortController.signal.aborted) return;

          throw e;
        }
      )
      .finally(() => setLoading(false));

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [offset, limit, isAuthenticated, residence.id]);

  useEffect(() => {
    if (!situations.length) return;

    setCurrentSituation((curr) => {
      const initialSituation =
        situations.find(
          (s) =>
            s.orientation.id === curr?.orientation.id &&
            s.relativePosition.id === curr?.relativePosition.id
        ) ??
        situations.find((s) => s.orientation.id === curr?.orientation.id) ??
        situations.find(
          (s) => s.relativePosition.id === curr?.relativePosition.id
        ) ??
        situations.at(0);

      setSelectedSituation(initialSituation);
      return initialSituation;
    });
  }, [situations, setCurrentSituation]);

  if (!currentSituation) return null;

  return (
    <Popover
      closeOnBlur
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      variant="info"
    >
      <PopoverTrigger>
        <Button
          _hover={{ bg: "brand.gray.90" }}
          bg="white"
          boxShadow="md"
          color="black"
          leftIcon={<Image src="/svg/compass.svg" />}
          onClick={onToggle}
        >
          Huidige configuratie:
          {currentSituation && (
            <Text as="span" ml={2}>
              {currentSituation.orientation.name} -{" "}
              {currentSituation.relativePosition.name}
            </Text>
          )}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent borderRadius={8} p={4} w="400px">
          <Text fontWeight="500">Situering</Text>
          <RadioGroup
            onChange={(e) =>
              setSelectedSituation(situations.find((s) => s.id === Number(e)))
            }
            value={selectedSituation?.id.toString() ?? ""}
          >
            <VStack alignItems="left" ml={-2} py={2} spacing={0}>
              {situations.map((s) => (
                <Radio
                  key={s.id}
                  color="brand.black.100"
                  defaultChecked={selectedSituation === s}
                  value={s.id.toString()}
                  w="min-content"
                >
                  {s.orientation.name} - {s.relativePosition.name}
                  <Text as="span" color="brand.gray.100" fontSize="sm" ml={2}>
                    ({s.scenarioCount})
                  </Text>
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
          <Button
            isDisabled={currentSituation === selectedSituation}
            onClick={() => {
              setCurrentSituation(selectedSituation);
              onClose();
              navigate(`/residence/${residence.id}/situation/${selectedSituation?.id}`);
            }}
            w="100%"
          >
            Bevestigen
          </Button>
          <Flex alignItems="center">
            {situations.length < totalCount && (
              <Button disabled={loading} onClick={loadMore}>
                Meer laden
              </Button>
            )}
            {loading && <Spinner display="block" ml={4} />}
          </Flex>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
